import { Award, BicepsFlexed, Drama, Frown, ThumbsDown } from 'lucide-react';
import { memo, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { DiscountStatus } from '@/pages/PurchasesContainer/RequestPerHand/utils/DiscountCheck';

type DiscountStatusBadgeProps = {
  discountStatus:
    | DiscountStatus
    | 'accept'
    | 'bad'
    | 'best'
    | 'only'
    | 'static'
    | undefined;
};

type StatusType = {
  bg: string;
  text: string;
  icon: React.ReactNode;
  border: string;
  glow: string;
  label: string;
};

const DiscountStatusBadge = memo(
  ({ discountStatus }: DiscountStatusBadgeProps) => {
    // Map discount status strings to enum values if needed - moved outside of conditional
    const statusValue = useMemo(() => {
      if (!discountStatus) return null;

      if (typeof discountStatus === 'string') {
        switch (discountStatus) {
          case 'accept':
            return DiscountStatus.ACCEPT;
          case 'bad':
            return DiscountStatus.BAD;
          case 'best':
            return DiscountStatus.BEST;
          case 'only':
            return DiscountStatus.ONLY;
          case 'static':
            return DiscountStatus.STATIC;
          default:
            return discountStatus;
        }
      }
      return discountStatus;
    }, [discountStatus]);

    // Map each discount status to its corresponding styles - moved outside of conditional
    const typeMapping = useMemo<Record<string, StatusType>>(
      () => ({
        [DiscountStatus.ACCEPT]: {
          bg: 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800/40 dark:to-gray-700/40',
          text: 'text-gray-700 dark:text-gray-200',
          icon: (
            <Frown className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          ),
          border: 'border-gray-300/30 dark:border-gray-500/30',
          glow: 'shadow-[0_0_8px_rgba(107,114,128,0.3)] dark:shadow-[0_0_12px_rgba(107,114,128,0.4)]',
          label: 'مقبول',
        },
        [DiscountStatus.BAD]: {
          bg: 'bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/40 dark:to-red-800/40',
          text: 'text-red-600 dark:text-red-300',
          icon: (
            <ThumbsDown className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          ),
          border: 'border-red-300/30 dark:border-red-500/30',
          glow: 'shadow-[0_0_8px_rgba(239,68,68,0.3)] dark:shadow-[0_0_12px_rgba(239,68,68,0.4)]',
          label: 'ضعيف',
        },
        [DiscountStatus.BEST]: {
          bg: 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/40 dark:to-green-800/40',
          text: 'text-green-700 dark:text-green-300',
          icon: (
            <Award className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          ),
          border: 'border-green-300/30 dark:border-green-500/30',
          glow: 'shadow-[0_0_8px_rgba(34,197,94,0.3)] dark:shadow-[0_0_12px_rgba(34,197,94,0.4)]',
          label: 'ممتاز',
        },
        [DiscountStatus.ONLY]: {
          bg: 'bg-gradient-to-br from-indigo-100 to-indigo-200 dark:from-indigo-900/40 dark:to-indigo-800/40',
          text: 'text-indigo-700 dark:text-indigo-300',
          icon: (
            <BicepsFlexed className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          ),
          border: 'border-indigo-300/30 dark:border-indigo-500/30',
          glow: 'shadow-[0_0_8px_rgba(99,102,241,0.3)] dark:shadow-[0_0_12px_rgba(99,102,241,0.4)]',
          label: 'فقط',
        },
        [DiscountStatus.STATIC]: {
          bg: 'bg-gradient-to-br from-fuchsia-100 to-fuchsia-200 dark:from-fuchsia-900/40 dark:to-fuchsia-800/40',
          text: 'text-fuchsia-700 dark:text-fuchsia-300',
          icon: (
            <Drama className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
          ),
          border: 'border-fuchsia-300/30 dark:border-fuchsia-500/30',
          glow: 'shadow-[0_0_8px_rgba(192,132,252,0.3)] dark:shadow-[0_0_12px_rgba(192,132,252,0.4)]',
          label: 'ثابت',
        },
      }),
      [],
    );

    if (!discountStatus) {
      return <span className="text-xs text-muted-foreground">غير نشط</span>;
    }

    // Get styles for the current discount status
    const typeData = statusValue ? typeMapping[statusValue] : null;

    if (!typeData) {
      return <span className="text-xs text-muted-foreground">غير معروف</span>;
    }

    return (
      <div
        title={typeData.label}
        className={cn(
          'group relative flex h-7 w-14 items-center justify-center overflow-hidden rounded-md border',
          typeData.border,
          typeData.bg,
          typeData.text,
          'font-medium transition-all duration-300 hover:scale-105',
        )}
      >
        {/* Decorative elements - improve animation performance */}
        <div className="from-current/30 absolute -left-0.5 -top-0.5 h-2 w-2 rounded-full bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></div>
        <div className="from-current/30 absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-gradient-to-tl to-transparent opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"></div>

        {/* Content */}
        <div className="z-1 relative">{typeData.icon}</div>
      </div>
    );
  },
);

DiscountStatusBadge.displayName = 'DiscountStatusBadge';

export default DiscountStatusBadge;
